import { useMemo, useState, useEffect } from 'react';
import { Select, Form } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UIHelper from 'helpers/UIHelper';
import AnalyticsApi from 'libs/api/analytics';
import { useRecoilValue } from 'recoil';
import { fileNameEdit } from 'helpers/FileUtils';
import { getShoppingCenters } from 'hooks/useUser';
import { useFilter } from 'hooks/useFilter';
import moment from 'moment';
import Helpers from 'helpers';

const apiCalendarCategories = new AnalyticsApi('calendar_categories/');
const calendarsApi = new AnalyticsApi('calendars/');

const { Option } = Select;

const data = [];

for (let i = 0; i < 10; i++) {
  data.push({
    key: i,
    id: i,
    date: new Date().toDateString(),
  });
}

const useCalendars = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [loadingSelect, setLoadingSelect] = useState('');
  const [calendarCategories, setCalendarCategories] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const { searchParams } = useFilter();
  const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
  const [avilableDates, setAvilableDates] = useState({
    next_date: '',
    prev_date: '',
  });
  const [loading, setLoading] = useState(false);

  const fetchData = async (hideAllLoading = false) => {
    if (!hideAllLoading) setLoading(true);

    try {
      const {
        data: { calendars = [], next_date, prev_date },
      } = await calendarsApi.list({
        params: {
          per: 10000,
          ...Object.fromEntries([...searchParams]),
          date,
        },
      });
      setCalendars(calendars);
      setAvilableDates({ next_date, prev_date });
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  function handleChange(id, type) {
    return async (value) => {
      setLoadingSelect(id);
      try {
        const data = {
          [type]: value,
        };

        await calendarsApi.put(id, { data });
        await fetchData(true);
      } catch (e) {
        const errors = e.response.data || [];
        form.setFields([
          {
            name: 'shopping_center_id',
            errors,
          },
        ]);
      } finally {
        setLoadingSelect('');
        UIHelper.openNotificationWithIcon('success', t('L’operazione è stata completata con successo'));
      }
    };
  }

  const columns = useMemo(() => {
    return [
      {
        title: t('Date'),
        dataIndex: 'calendar_date',
      },
      {
        title: t('Giorno'),
        dataIndex: 'day_type',
        render: (data, row) => {
          return (
            <Select
              loading={row.id === loadingSelect}
              defaultValue=''
              style={{ width: '100%' }}
              onChange={handleChange(row.id, 'day_type')}
              name={'day_type'}
              placeholder={t('Selezioni opzione')}
              value={data}
            >
              <Option value=''>{t('Selezioni opzione')}</Option>
              <Option selected='selected' value='work_day'>
                {t('Feriale')}
              </Option>
              <Option value='day_off'>{t('Chiusura')}</Option>
              <Option value='holiday'>{t('Festivo')}</Option>
              <Option value='preholiday'>{t('Prefestivo')}</Option>
            </Select>
          );
        },
      },
      {
        title: t('Trading calendar'),
        dataIndex: 'analytics_calendar_category_id',
        render: (data, row) => {
          return (
            <Select
              loading={row.id === loadingSelect}
              style={{ width: '100%' }}
              onChange={handleChange(row.id, 'analytics_calendar_category_id')}
              placeholder={t('Selezioni opzione')}
              name={'analytics_calendar_category_id'}
              value={data}
            >
              <Option value={''}>{t('Selezioni opzione')}</Option>
              {calendarCategories.map((item) => {
                return (
                  <Option value={item.id} key={item.id}>
                    {item.title}
                  </Option>
                );
              })}
            </Select>
          );
        },
      },
    ];
  }, [pathname, loadingSelect, calendarCategories, calendars]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const {
          data: { calendar_categories = [] },
        } = await apiCalendarCategories.list({
          params: {
            per: 10000,
          },
        });
        setCalendarCategories(calendar_categories);
      } catch (e) {
        console.log(e);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [date, searchParams]);

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      const response = await calendarsApi.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdateCategory = async (values, id) => {
    setLoading(true);
    try {
      const data = {
        ...values,
      };

      await calendarsApi.create({ data });
      navigate(
        UIHelper.getListRoute(pathname, 1) +
          `?by_shopping_center=${values.shopping_center_id}`,
      );
    } catch (e) {
      const errors = e.response.data || [];
      form.setFields([
        {
          name: 'shopping_center_id',
          errors,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return {
    columns,
    data,
    shoppingCenters,
    searchParams,
    calendars,
    setDate,
    avilableDates,
    loading,
    handleDownloadExcel,
    form,
    handleCreateOrUpdateCategory,
  };
};

export default useCalendars;
